"use client";

import logo from "@/public/images/logo.png";
import styles from "@/src/app/fluentui-styles";
import FormErrorText from "@/src/components/form/FormErrorText";
import FormInput from "@/src/components/form/FormInput";
import FormPasswordInput from "@/src/components/form/FormPasswordInput";
import useAuthRedirect from "@/src/hooks/use-auth-redirect";
import { useToast } from "@/src/hooks/use-toast";
import AppToast from "@/src/components/AppToast";
import useStore from "@/src/store/server/cargogo/auth/loginStore";
import { User } from "@/src/store/type";
import { handleRequestError } from "@/src/util/handle-request-error";
import { parseIntlJSON } from "@/src/util/i18n";
import {
  LoginFormValues as FormValues,
  LoginSchema as Schema,
} from "@/src/zod-schema/user/login-schema";
import {
  Body1,
  Button,
  Caption1,
  Card,
  Checkbox,
  Divider,
  makeStyles,
  Spinner,
  Title1,
} from "@fluentui/react-components";
import { LockClosedRegular, PersonRegular } from "@fluentui/react-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { deleteCookie } from "cookies-next/client";
import * as jose from "jose";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Controller, useForm } from "react-hook-form";
import { useEffect } from "react";
import { resetAuthSessionInvalidation } from "@/src/util/auth-session";
import { resetClientSessionState } from "@/src/util/reset-client-session";

const useStyles = makeStyles({
  ...styles,
  card: {
    ...styles.card,
    width: "100%",
    maxWidth: "400px",
    padding: "24px",
  },
  label: {
    ...styles.label,
    width: "100%",
  },
  button: {
    ...styles.button,
    width: "100%",
  },
});

const LoginPage = () => {
  //
  const router = useRouter();
  const classes = useStyles();
  const t = useTranslations();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(Schema()),
    defaultValues: { remember_me: false },
  });

  const { success, msg, loading, request } = useStore();

  const { showToast } = useToast();

  useAuthRedirect();

  useEffect(() => {
    resetAuthSessionInvalidation();
    void resetClientSessionState();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("reason") !== "session_expired") return;

    showToast(
      <AppToast title={t("error.auth.session_expired")} />,
      { position: "bottom-end", intent: "warning" },
      true,
    );

    params.delete("reason");
    const next = params.toString();
    window.history.replaceState({}, "", next ? `/login?${next}` : "/login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data: FormValues) => {
    request(data.email, data.password, data.remember_me)
      .then((res) => {
        if (res.success && res.data) {
          deleteCookie("last-visited");
          window.location.href = "/";
        }
      })
      .catch((err) => handleRequestError(err, showToast, t, router));
  };

  // console.log("[DEBUG] Render Login Page");

  return (
    <div className="flex flex-col items-center justify-center w-full">
      <div className="flex flex-col items-center justify-center mb-4">
        <Image className="py-2 mb-4" src={logo} alt="Logo" layout="fixed" width={164} height={32} />
      </div>
      <Card className={classes.card}>
        <form>
          <div>
            <div className="mb-1">
              <Title1>{t("auth.btn.login")}</Title1>
            </div>
            <div className="mb-4">
              <Body1 style={{ color: "var(--colorNeutralForeground3)" }}>
                {t("auth.welcome_back_msg")}
              </Body1>
            </div>
            <div className="mb-4">
              <Controller
                control={control}
                name="email"
                render={({ field: { ref, value, onChange } }) => (
                  <FormInput
                    ref={ref}
                    required
                    data-testid="login-email"
                    label={t("common.email")}
                    value={value ?? ""}
                    placeholder={t("common.placeholder.enter_your", {
                      label: t("common.email").toLowerCase(),
                    })}
                    validationState={errors.email ? "error" : "none"}
                    validationMessage={errors.email ? parseIntlJSON(t, errors.email.message) : ""}
                    contentBefore={<PersonRegular />}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <div className="mb-2">
              <Controller
                control={control}
                name="password"
                render={({ field: { value, onChange } }) => (
                  <FormPasswordInput
                    required
                    data-testid="login-password"
                    label={t("common.password")}
                    value={value ?? ""}
                    placeholder={t("common.placeholder.enter_your", {
                      label: t("common.password").toLowerCase(),
                    })}
                    validationState={errors.password ? "error" : "none"}
                    validationMessage={
                      errors.password ? parseIntlJSON(t, errors.password.message) : ""
                    }
                    contentBefore={<LockClosedRegular />}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <div className="mb-4 flex items-center justify-between gap-2">
              <Controller
                control={control}
                name="remember_me"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    className={classes.checkbox}
                    size="medium"
                    label={t("auth.btn.remember_me")}
                    onChange={onChange}
                    defaultChecked={value}
                  />
                )}
              />
              <Link href="/forgotpassword">
                <Caption1 style={{ color: "var(--colorBrandForeground1)" }}>
                  {t("auth.btn.forgot_password")}?
                </Caption1>
              </Link>
            </div>
            <FormErrorText success={success} text={msg} />
            <div className="mb-2">
              <Button
                className={classes.button}
                size="medium"
                appearance="primary"
                type="submit"
                data-testid="login-submit"
                onClick={handleSubmit(onSubmit)}
              >
                {t("auth.btn.login")}
                {loading && <Spinner className="ms-2" size="extra-tiny" />}
              </Button>
            </div>
            <Divider className={classes.divider} />
            <Link href="/register" className="block w-full">
              <Button className={classes.button} size="medium">
                {t("auth.btn.register")}
              </Button>
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default LoginPage;
